import { Image, SizeOptionsRequest, Size, ServiceLevel } from '~/lib/ApiModel';
import { hasValue, orderByLookup } from './ArrayHelper';
import { applySizeDefaultsToOptions } from './SizeHelper';

const distroLogos: Record<string, string | undefined> = {
    ubuntu: '/img/logos/ubuntu.svg',
    centos: '/img/logos/centos.svg',
    'cpanel+whm': '/img/logos/cpanel.svg',
    windows: '/img/logos/windows.svg',
    debian: '/img/logos/debian.svg',
    fedora: '/img/logos/fedora.svg',
    freebsd: '/img/logos/freebsd.svg',
    opensuse: '/img/logos/opensuse.svg',
    kde: '/img/logos/kde.svg',
    kdeneon: '/img/logos/kdeneon.svg',
    rocky: '/img/logos/rocky.svg',
    almalinux: '/img/logos/alma.svg',
    byo: '/img/logos/byo.svg',
};

const distroLogoFallback = '/img/logos/byo.svg';

const distroDisplayOrder = ['ubuntu', 'debian', 'cpanel+whm', 'windows', 'freebsd', 'byo'];

export function logoForDistribution(distribution: string | null | undefined): string {
    return distroLogos[distribution?.toLowerCase() ?? ''] ?? distroLogoFallback;
}

export function versionForOperatingSystem(os: Image): string {
    return os.name || 'Latest';
}

export function distributionsFromOperatingSystems(oses: Image[]): string[] {
    const distributions: string[] = [];
    for (const dist of oses.map((o) => o.distribution)) {
        if (dist && distributions.indexOf(dist) === -1) {
            distributions.push(dist);
        }
    }
    return orderByLookup(distributions, distroDisplayOrder, (d) => d.toLowerCase());
}

export function operatingSystemsForDistribution(distribution: string, oses: Image[]) {
    const versions = oses.filter((os) => os.distribution === distribution);

    return versions.sort(osVersionSorter);
}

function splitSlug(slug: string): [string, number | undefined, string] {
    const [name = '', ...rest] = slug.split('-');
    const versionNumberString = slug.match(/([0-9]+([.][0-9]+)?)/)?.[0];
    const versionString = rest.join('-');
    return [name, hasValue(versionNumberString) ? Number(versionNumberString) : undefined, versionString];
}

export function osVersionSorter(a: Image, b: Image): number {
    // All oses should have slugs, but if they don't we just fall back to name sort
    const aslug = a.slug;
    const bslug = b.slug;

    if (!aslug || !bslug) {
        return b.name.localeCompare(a.name);
    }

    const [aName, aVersionNumber, aVersionString] = splitSlug(aslug);
    const [bName, bVersionNumber, bVersionString] = splitSlug(bslug);

    if (aName !== bName) {
        return aName.localeCompare(bName); // ascending by name
    } else if (hasValue(aVersionNumber) && hasValue(bVersionNumber) && aVersionNumber !== bVersionNumber) {
        return bVersionNumber - aVersionNumber; // descending by version number
    } else {
        return aVersionString.localeCompare(bVersionString); // ascending by version string
    }
}

export function isWindows(os: Image): boolean {
    return /windows/i.test(os.name);
}

export function isByo(os: Image): boolean {
    return /byo/i.test(os.distribution ?? '');
}

export function getOperatingSystemSurcharge(
    os: Image,
    size: Size,
    selectedOptions: SizeOptionsRequest,
    serviceLevel?: ServiceLevel
): number {
    const opts = applySizeDefaultsToOptions(size, selectedOptions);
    const surcharges = os.distribution_surcharges;
    if (!surcharges) return 0;

    const planOsSurchargeCredit = size.distribution_surcharge_credit_monthly ?? 0;
    const serviceLevelSurchargeCredit = serviceLevel?.distribution_surcharge_credit_monthly ?? 0;
    const totalSurchargeCredit = planOsSurchargeCredit + serviceLevelSurchargeCredit;

    let surchargeCost = surcharges.surcharge_base_cost ?? 0;
    if (surcharges.surcharge_per_memory_megabyte) {
        const memoryForSurcharge = surcharges.surcharge_per_memory_max_megabytes
            ? Math.min(surcharges.surcharge_per_memory_max_megabytes, opts.memory)
            : opts.memory;
        surchargeCost = surchargeCost + surcharges.surcharge_per_memory_megabyte * memoryForSurcharge;
    }

    if (surcharges.surcharge_per_vcpu) {
        const cpusForSurcharge = Math.max(size.vcpus, surcharges.surcharge_min_vcpu ?? 0);
        surchargeCost = surchargeCost + surcharges.surcharge_per_vcpu * cpusForSurcharge;
    }

    return Math.max(0, surchargeCost - totalSurchargeCredit);
}

export function supportsSsh(os: Image): boolean {
    return os.distribution_info.features.indexOf('ssh') >= 0;
}

export function supportsSshKeys(os: Image): boolean {
    return supportsSsh(os);
}

export function supportsRdp(os: Image): boolean {
    return os.distribution_info.features.indexOf('remote-desktop') >= 0;
}

export function supportsUserData(os: Image): boolean {
    return os.distribution_info.features.indexOf('user-data') >= 0;
}
